import './settings-leaf.css';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { Alert, Button, Checkbox, TextInput, LoadingBlock } from '../ui.tsx';
import { MAX_DIGEST_LOCATIONS } from '../../../shared/tomorrow-digest.ts';
import type {
  TomorrowDigestPreference,
  TomorrowDigestSettings,
  TomorrowDigestUpdate,
} from '../../../shared/tomorrow-digest.ts';

export default function TomorrowDigestCard() {
  const { t } = useTranslation();
  const { company, user, impersonatedBy } = useSession();
  const client = useQueryClient();
  const key = ['tomorrow-digest', company?.id, user?.id];
  const query = useQuery({
    queryKey: key,
    queryFn: () => api.getFresh<TomorrowDigestSettings>('/me/tomorrow-digest'),
    staleTime: 0,
  });
  const [draft, setDraft] = useState<TomorrowDigestSettings | null>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    setDraft(query.data ?? null);
  }, [query.data]);
  const mutation = useMutation({
    mutationFn: (value: TomorrowDigestUpdate) => api.put<TomorrowDigestSettings>('/me/tomorrow-digest', value),
    onSuccess: (value) => {
      client.setQueryData(key, value);
      setDraft(value);
      setSaved(true);
    },
  });
  function change<K extends keyof TomorrowDigestPreference>(field: K, value: TomorrowDigestPreference[K]) {
    setSaved(false);
    setDraft((current) => (current ? { ...current, [field]: value } : current));
  }
  const maxLocations = draft?.maxLocations ?? (draft?.multiLocation ? MAX_DIGEST_LOCATIONS : 1);
  const selectionTooLarge = (draft?.locationIds.length ?? 0) > maxLocations;
  if (query.data && !query.data.locations.length && !query.data.enabled) return null;
  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="tomorrow-digest-heading">
      <div className="work-section-heading">
        <h2 id="tomorrow-digest-heading" className="mb-2 text-lg font-semibold">
          {t('tomorrowDigest.title')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('tomorrowDigest.description')}</p>
      {query.isLoading && <LoadingBlock label={t('tomorrowDigest.loading')} />}
      {query.isError && (
        <Alert tone="error" title={t('tomorrowDigest.loadError')}>
          <Button onClick={() => void query.refetch()}>{t('tomorrowDigest.retry')}</Button>
        </Alert>
      )}
      {draft && (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            const { enabled, sendTime, timezone, locationIds, sendEmpty } = draft;
            mutation.mutate(enabled ? { enabled, sendTime, timezone, locationIds, sendEmpty } : { enabled: false });
          }}
        >
          <fieldset disabled={mutation.isPending || !!impersonatedBy}>
            <Checkbox
              label={t('tomorrowDigest.title')}
              checked={draft.enabled}
              disabled={!draft.available && !draft.enabled}
              onChange={(e) => change('enabled', e.target.checked)}
            />
            <p className="mb-4 text-sm">{t('tomorrowDigest.recipient', { email: user?.email })}</p>
            {!draft.available && <p className="mb-4 text-sm">{t('tomorrowDigest.unavailable')}</p>}
            {draft.enabled && (draft.selectionNeedsReview || selectionTooLarge) && (
              <Alert tone="warning">{t('tomorrowDigest.selectionNeedsReview')}</Alert>
            )}
            <fieldset disabled={!draft.enabled}>
              <TextInput
                label={t('tomorrowDigest.sendTime')}
                type="time"
                min="16:00"
                max="21:59"
                required
                value={draft.sendTime}
                onChange={(e) => change('sendTime', e.target.value)}
              />
              <TextInput
                label={t('tomorrowDigest.timezone')}
                hint={t('tomorrowDigest.timezoneHint')}
                required
                value={draft.timezone}
                onChange={(e) => change('timezone', e.target.value)}
              />
              <fieldset className="mb-4">
                <legend className="mb-2 font-semibold">{t('tomorrowDigest.locations')}</legend>
                <p className="mb-2 text-sm">
                  {t('tomorrowDigest.locationLimit', {
                    selected: draft.locationIds.length,
                    total: draft.locations.length,
                    limit: maxLocations,
                  })}
                </p>
                {draft.locations.map((location) => (
                  <Checkbox
                    key={location.id}
                    label={`${location.name} (${location.timezone})`}
                    checked={draft.locationIds.includes(location.id)}
                    disabled={
                      draft.multiLocation &&
                      !draft.locationIds.includes(location.id) &&
                      draft.locationIds.length >= maxLocations
                    }
                    onChange={(e) => {
                      const ids = e.target.checked
                        ? draft.multiLocation
                          ? [...draft.locationIds, location.id]
                          : [location.id]
                        : draft.locationIds.filter((id) => id !== location.id);
                      setSaved(false);
                      setDraft({
                        ...draft,
                        locationIds: ids,
                        timezone:
                          ids.length === 1
                            ? draft.locations.find((l) => l.id === ids[0])!.timezone
                            : draft.companyTimezone,
                      });
                    }}
                  />
                ))}
              </fieldset>
              <Checkbox
                label={t('tomorrowDigest.sendEmpty')}
                checked={draft.sendEmpty}
                onChange={(e) => change('sendEmpty', e.target.checked)}
              />
            </fieldset>
            <p className="my-4 text-sm text-[color:var(--color-muted)]">{t('tomorrowDigest.privacy')}</p>
            {mutation.isError && <Alert tone="error">{t('tomorrowDigest.saveError')}</Alert>}
            {saved && <p role="status">{t('tomorrowDigest.saved')}</p>}
            <Button
              type="submit"
              disabled={draft.enabled && (!draft.available || !draft.locationIds.length || selectionTooLarge)}
            >
              {t('tomorrowDigest.save')}
            </Button>
          </fieldset>
        </form>
      )}
    </section>
  );
}
